import maplibregl from 'maplibre-gl';
import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store from '../state';
import LiveWeatherService, { parseIsoScanToMs } from '../services/LiveWeatherService';
import ManifestTimelineHelper from '../services/ManifestTimelineHelper';
import { getTileUrl, getRtmaWindGridUrl, buildPmtilesUrl, cleanUrl, appendAuthToken, getApiServer, getSatelliteOverlayTileUrl } from '../config';
import CanvasRadarRenderer from '../utils/CanvasRadarRenderer';
import CanvasSatelliteRenderer from '../utils/CanvasSatelliteRenderer';
import VectorLightningRenderer from '../utils/VectorLightningRenderer';
import CanvasWindRenderer from '../utils/CanvasWindRenderer';
import { aqiStationModal } from '../ui/panels/AqiStationModal';
import ForecastAnimationService from '../services/ForecastAnimationService';

export class LiveWeatherLayer extends BaseProduct {
  public readonly id: ProductId = 'live_weather';
  private service: LiveWeatherService;
  private radarRenderer: CanvasRadarRenderer;
  private satRenderer: CanvasSatelliteRenderer;
  private lightningRenderer: VectorLightningRenderer;
  private rtmaWindRenderer: CanvasWindRenderer;
  private hoverPopup: maplibregl.Popup | null = null;
  private aqiInteractionsBound: boolean = false;
  
  private observedIntervalId: any = null;
  private observedAnimationToken: number = 0;
  private isObservedBuffering: boolean = false;
  private refreshedDuringStoreUpdate: boolean = false;
  private onMapMoveBound: () => void;
  private lastQpeMode: string = 'off';
  private lastAqiMtimeEpoch: number = 0;

  constructor(map: maplibregl.Map, layerManager: any) {
    super(map, layerManager);
    this.service = LiveWeatherService.getInstance();
    this.radarRenderer = new CanvasRadarRenderer(map);
    this.satRenderer = new CanvasSatelliteRenderer(map);
    this.lightningRenderer = new VectorLightningRenderer(map);
    this.rtmaWindRenderer = new CanvasWindRenderer(map);
    this.onMapMoveBound = this.handleMapMove.bind(this);
    (window as any).liveWeatherLayer = this;
  }

  private getFirstSymbolLayerId(): string | undefined {
    const layers = this.map.getStyle()?.layers;
    if (!layers) return undefined;
    for (const layer of layers) {
      if (layer.type === 'symbol') {
        return layer.id;
      }
    }
    return undefined;
  }

  private unsubscribeStore: (() => void) | null = null;

  protected onActivate(): void {
    const state = store.getState();
    const activeSatSources = state.liveWeatherSatelliteSources || [state.liveWeatherSatelliteSource || 'goes_composite'];
    if (state.liveWeatherSatelliteEnabled) {
      this.service.getSatelliteFrames(
        undefined,
        activeSatSources
      );
    }

    this.service.fetchRadarMetadata().then((frames) => {
      if (frames.length > 0) {
        this.service.setCurrentFrameIndex(frames.length - 1);
      }
      this.refresh();
      const curState = store.getState();
      if (curState.liveWeatherRadarEnabled && curState.liveWeatherRadarPlaying) {
        this.radarRenderer.preloadSequence(frames);
      }
      store.update({ liveWeatherRadarFrameCount: frames.length });
      
      // Re-trigger timeline update now that radar frames are loaded
      this.updateLiveWeatherTimeline();
    });

    let lastSatEnabled = state.liveWeatherSatelliteEnabled;
    let lastRadarEnabled = state.liveWeatherRadarEnabled;
    let lastLightningEnabled = state.liveWeatherRadarLightningEnabled;
    let lastRtmaWindsEnabled = state.liveWeatherRtmaWindsEnabled;
    let lastRtmaWindsOpacity = state.liveWeatherRtmaWindsOpacity;
    let lastRtmaWindsMode = state.liveWeatherRtmaWindsMode;
    let lastAqiStationsEnabled = state.liveWeatherAqiStationsEnabled;
    let lastAqiLabelsEnabled = state.aqiStationLabelsEnabled;
    let lastSunIntensityEnabled = state.liveWeatherSunIntensityEnabled;
    let lastSatSources = JSON.stringify(activeSatSources);
    let lastSatRenderMode = state.liveWeatherSatRenderMode;
    let lastSatLoopHours = state.liveWeatherSatelliteLoopHours || 8;
    let lastSatPlaybackSpeed = state.liveWeatherSatPlaybackSpeed;
    let lastSatOpacity = state.liveWeatherSatelliteOpacity;
    let lastSatOverlayHigh = state.liveWeatherSatOverlayHighEnabled;
    let lastSatOverlayMid = state.liveWeatherSatOverlayMidEnabled;
    let lastSatOverlayFog = state.liveWeatherSatOverlayFogEnabled;
    let lastSatOverlayHighOpacity = state.liveWeatherSatOverlayHighOpacity;
    let lastSatOverlayMidOpacity = state.liveWeatherSatOverlayMidOpacity;
    let lastSatOverlayFogOpacity = state.liveWeatherSatOverlayFogOpacity;
    let lastRadarPlaybackSpeed = state.liveWeatherRadarPlaybackSpeed;
    let lastRadarLoopHours = state.liveWeatherRadarLoopHours;
    let lastRadarOpacity = state.liveWeatherRadarOpacity;
    let lastLightningOpacity = state.liveWeatherLightningOpacity;
    let lastQpeMode = state.liveWeatherQpeMode;
    let lastQpeOpacity = state.liveWeatherQpeOpacity;
    let lastSessionToken = state.sessionToken;
    let lastManifest = state.manifest;
    let lastSatPlaying = state.liveWeatherSatellitePlaying;
    let lastRadarPlaying = state.liveWeatherRadarPlaying;

    this.unsubscribeStore = store.subscribe((s) => {
      if (!this.active) return;
      if (s.sessionToken !== lastSessionToken) {
        lastSessionToken = s.sessionToken;
        this.radarRenderer.clearCache();
        this.satRenderer.clearCache();
        this.lightningRenderer.clearCache();
        this.rtmaWindRenderer.clearCache();
        this.updateLiveWeatherTimeline();
        this.refresh();
        return;
      }
      if (s.manifest !== lastManifest) {
        // Background manifest refresh (ManifestRefreshService) published new observed scans.
        lastManifest = s.manifest;
        this.onManifestRefreshed();
        return;
      }
      const currentSourcesStr = JSON.stringify(s.liveWeatherSatelliteSources || [s.liveWeatherSatelliteSource || 'goes_composite']);
      const satSourcesChanged = currentSourcesStr !== lastSatSources;
      const satModeChanged = s.liveWeatherSatRenderMode !== lastSatRenderMode;
      const satLoopChanged = s.liveWeatherSatelliteLoopHours !== lastSatLoopHours;
      const satEnabledChanged = s.liveWeatherSatelliteEnabled !== lastSatEnabled;
      const radarEnabledChanged = s.liveWeatherRadarEnabled !== lastRadarEnabled;
      const lightningEnabledChanged = s.liveWeatherRadarLightningEnabled !== lastLightningEnabled;
      const rtmaWindsEnabledChanged = s.liveWeatherRtmaWindsEnabled !== lastRtmaWindsEnabled;
      const rtmaWindsOpacityChanged = s.liveWeatherRtmaWindsOpacity !== lastRtmaWindsOpacity;
      const rtmaWindsModeChanged = s.liveWeatherRtmaWindsMode !== lastRtmaWindsMode;
      const aqiStationsEnabledChanged = s.liveWeatherAqiStationsEnabled !== lastAqiStationsEnabled;
      const aqiLabelsChanged = s.aqiStationLabelsEnabled !== lastAqiLabelsEnabled;
      const sunIntensityEnabledChanged = s.liveWeatherSunIntensityEnabled !== lastSunIntensityEnabled;
      const qpeModeChanged = s.liveWeatherQpeMode !== lastQpeMode;
      const qpeOpacityChanged = s.liveWeatherQpeOpacity !== lastQpeOpacity;

      const satOverlaysChanged = (
        s.liveWeatherSatOverlayHighEnabled !== lastSatOverlayHigh ||
        s.liveWeatherSatOverlayMidEnabled !== lastSatOverlayMid ||
        s.liveWeatherSatOverlayFogEnabled !== lastSatOverlayFog
      );
      const satOverlayOpacitiesChanged = (
        s.liveWeatherSatOverlayHighOpacity !== lastSatOverlayHighOpacity ||
        s.liveWeatherSatOverlayMidOpacity !== lastSatOverlayMidOpacity ||
        s.liveWeatherSatOverlayFogOpacity !== lastSatOverlayFogOpacity
      );

      if (satEnabledChanged || radarEnabledChanged || lightningEnabledChanged || rtmaWindsEnabledChanged || aqiStationsEnabledChanged || aqiLabelsChanged || sunIntensityEnabledChanged || satSourcesChanged || satModeChanged || satLoopChanged || qpeModeChanged || satOverlaysChanged) {
        lastSatEnabled = s.liveWeatherSatelliteEnabled;
        lastRadarEnabled = s.liveWeatherRadarEnabled;
        lastLightningEnabled = s.liveWeatherRadarLightningEnabled;
        lastRtmaWindsEnabled = s.liveWeatherRtmaWindsEnabled;
        lastAqiStationsEnabled = s.liveWeatherAqiStationsEnabled;
        lastAqiLabelsEnabled = s.aqiStationLabelsEnabled;
        lastSunIntensityEnabled = s.liveWeatherSunIntensityEnabled;
        lastSatSources = currentSourcesStr;
        lastSatRenderMode = s.liveWeatherSatRenderMode;
        lastSatLoopHours = s.liveWeatherSatelliteLoopHours;
        lastQpeMode = s.liveWeatherQpeMode;
        lastSatOverlayHigh = s.liveWeatherSatOverlayHighEnabled;
        lastSatOverlayMid = s.liveWeatherSatOverlayMidEnabled;
        lastSatOverlayFog = s.liveWeatherSatOverlayFogEnabled;

        // Immediately terminate observed playback if all animatable observed layers were turned off
        if ((satEnabledChanged || radarEnabledChanged || lightningEnabledChanged) && !this.hasAnimatableObservedLayers(s)) {
          lastSatPlaying = false;
          lastRadarPlaying = false;
          this.stopObservedAnimation(true);
          return;
        }

        // If already playing and enabled observed layers or satellite feeds changed, re-anchor loop cleanly
        if (
          (this.observedIntervalId !== null || this.isObservedBuffering) &&
          (satEnabledChanged || radarEnabledChanged || lightningEnabledChanged || satSourcesChanged || satLoopChanged)
        ) {
          void this.startObservedAnimation(true);
        }

        this.updateLiveWeatherTimeline();
        this.refresh();
      } else if (
        s.liveWeatherSatelliteOpacity !== lastSatOpacity ||
        s.liveWeatherSatPlaybackSpeed !== lastSatPlaybackSpeed ||
        s.liveWeatherRadarPlaybackSpeed !== lastRadarPlaybackSpeed ||
        s.liveWeatherRadarLoopHours !== lastRadarLoopHours ||
        s.liveWeatherRadarOpacity !== lastRadarOpacity ||
        s.liveWeatherLightningOpacity !== lastLightningOpacity ||
        rtmaWindsOpacityChanged ||
        rtmaWindsModeChanged ||
        qpeOpacityChanged ||
        satOverlayOpacitiesChanged
      ) {
        const speedOrLoopChanged =
          s.liveWeatherSatPlaybackSpeed !== lastSatPlaybackSpeed ||
          s.liveWeatherRadarPlaybackSpeed !== lastRadarPlaybackSpeed ||
          s.liveWeatherRadarLoopHours !== lastRadarLoopHours;
        lastSatOpacity = s.liveWeatherSatelliteOpacity;
        lastSatPlaybackSpeed = s.liveWeatherSatPlaybackSpeed;
        lastRadarPlaybackSpeed = s.liveWeatherRadarPlaybackSpeed;
        lastRadarLoopHours = s.liveWeatherRadarLoopHours;
        lastRadarOpacity = s.liveWeatherRadarOpacity;
        lastLightningOpacity = s.liveWeatherLightningOpacity;
        lastRtmaWindsOpacity = s.liveWeatherRtmaWindsOpacity;
        lastRtmaWindsMode = s.liveWeatherRtmaWindsMode;
        lastQpeOpacity = s.liveWeatherQpeOpacity;
        lastSatOverlayHighOpacity = s.liveWeatherSatOverlayHighOpacity;
        lastSatOverlayMidOpacity = s.liveWeatherSatOverlayMidOpacity;
        lastSatOverlayFogOpacity = s.liveWeatherSatOverlayFogOpacity;
        if (speedOrLoopChanged && (this.observedIntervalId !== null || this.isObservedBuffering)) {
          void this.startObservedAnimation(true);
        }
        this.refresh();
      }

      const wasAnyPlaying = Boolean(lastSatPlaying || lastRadarPlaying);
      const isAnyPlaying = Boolean(s.liveWeatherSatellitePlaying || s.liveWeatherRadarPlaying);
      if (s.liveWeatherSatellitePlaying !== lastSatPlaying || s.liveWeatherRadarPlaying !== lastRadarPlaying) {
        lastSatPlaying = s.liveWeatherSatellitePlaying;
        lastRadarPlaying = s.liveWeatherRadarPlaying;
        if (!isAnyPlaying) {
          this.observedAnimationToken++;
          this.isObservedBuffering = false;
          if (this.observedIntervalId) {
            clearInterval(this.observedIntervalId);
            this.observedIntervalId = null;
          }
          if (s.liveWeatherObservedBuffering) {
            store.update({ liveWeatherObservedBuffering: false });
          }
        } else if (!wasAnyPlaying && !this.observedIntervalId && !this.isObservedBuffering) {
          void this.startObservedAnimation(false);
        }
      }
    });

    if ((state.liveWeatherRadarPlaying || state.liveWeatherSatellitePlaying) && this.hasAnimatableObservedLayers(state)) {
      void this.startObservedAnimation(false);
    }

    this.map.on('moveend', this.onMapMoveBound);
  }

  /** Build and push live weather timeline range into global store */
  public updateLiveWeatherTimeline(): void {
    const state = store.getState();
    const liveService = this.service;

    const satSources = state.liveWeatherSatelliteSources || [state.liveWeatherSatelliteSource || 'goes_composite'];
    const satFrames = state.liveWeatherSatelliteEnabled
      ? liveService.getSatelliteFrames(undefined, satSources)
      : [];
    if (satFrames.length > 0 && state.liveWeatherSatellitePlaying) {
      this.satRenderer.preloadSequence(satFrames);
    }
    if (state.liveWeatherRadarEnabled) {
      const radarFrames = liveService.getRadarFrames();
      if (radarFrames.length > 0 && state.liveWeatherRadarPlaying) {
        this.radarRenderer.preloadSequence(radarFrames);
      }
    }

    // Keep master static timelineRange sacrosanct. Only refresh frames.
    if (!state.currentTimeStep) {
      store.update({ currentTimeStep: ManifestTimelineHelper.getNowDate(state.timelineRange) });
    }
  }

  /**
   * Applies a refreshed manifest: rebuilds radar/satellite frame lists (and preloads
   * new frames), then re-resolves every live sub-layer from the new manifest. When the
   * user is parked at NOW this advances the display to the newest scan; a scrubbed-back
   * time is preserved. Running loops are re-anchored because they capture the latest
   * scan timestamp at start, which would otherwise mis-map frames once new scans arrive.
   */
  private onManifestRefreshed(): void {
    const state = store.getState();
    this.updateLiveWeatherTimeline();
    if (state.liveWeatherRadarEnabled) {
      store.update({ liveWeatherRadarFrameCount: this.service.getRadarFrames().length });
    }
    const mtime = Number((state.manifest as any)?.aqi_stations?.mtime_epoch) || 0;
    if (mtime > 0 && mtime !== this.lastAqiMtimeEpoch && this.map.getSource('live-weather-aqi-stations-source')) {
      this.lastAqiMtimeEpoch = mtime;
      void this.service.fetchAqiStationsGeoJson(true).then((geoJson) => {
        const src = this.map.getSource('live-weather-aqi-stations-source') as maplibregl.GeoJSONSource | undefined;
        if (src && geoJson) {
          src.setData(geoJson);
        }
      });
    }
    if (this.observedIntervalId !== null) {
      void this.startObservedAnimation(true);
    }
    this.refresh();
  }



  protected onDeactivate(): void {
    this.stopObservedAnimation(true);
    this.map.off('moveend', this.onMapMoveBound);
    this.rtmaWindRenderer.clear();
    this.satRenderer.clear();

    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }

    this.radarRenderer.clear();
    this.lightningRenderer.clear();
    this.layerManager.removeLayer('live-weather-temp-gradient');
    this.layerManager.removeLayer('live-weather-mrms-lightning');
    this.layerManager.removeLayer('live-weather-sun-intensity');
    this.layerManager.removeBufferedRasterLayer('live-weather-mrms-qpe');
    this.layerManager.removeLayer('live-weather-sat-overlay-high');
    this.layerManager.removeLayer('live-weather-sat-overlay-mid');
    this.layerManager.removeLayer('live-weather-sat-overlay-fog');
    this.removeAqiStationsLayer();
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh(): void {
    this.update(store.getState());
  }

  private updateStoreAndRefreshOnce(patch: any): void {
    this.refreshedDuringStoreUpdate = false;
    store.update(patch);
    if (!this.refreshedDuringStoreUpdate && this.active) {
      this.refresh();
    }
  }

  public update(state: any): void {
    if (!this.active) return;
    this.refreshedDuringStoreUpdate = true;

    // 0. Update RTMA Current Surface Winds Layer (Particle animation)
    if (state.liveWeatherRtmaWindsEnabled) {
      const gridUrl = getRtmaWindGridUrl(state.manifest, state.currentTimeStep);
      this.rtmaWindRenderer.setGridUrl(gridUrl, state.liveWeatherRtmaWindsOpacity, state.liveWeatherRtmaWindsMode);
    } else {
      this.rtmaWindRenderer.clear();
    }

    // 1. Update Satellite Layer
    if (state.liveWeatherSatelliteEnabled) {
      if (state.currentTimeStep) {
        const closestIdx = this.service.getClosestSatelliteFrameIndex(state.currentTimeStep);
        this.service.setSatelliteFrameIndex(closestIdx);
      }
      const satFrame = this.service.getCurrentSatelliteFrame();
      const satUrl = satFrame ? satFrame.tileUrl : null;
      this.layerManager.removeLayer('live-weather-satellite-pmtiles');
      this.satRenderer.update(
        satUrl,
        state.liveWeatherSatelliteOpacity,
        true,
        state.liveWeatherSatelliteSource || 'goes_composite',
        satFrame?.blendUrls
      );

      // 1b. Satellite Cloud Deck Discrete Overlays (High Clouds, Mid Clouds, Valley Fog Sea)
      const satScan = satFrame?.path || null;
      const firstSymbol = this.getFirstSymbolLayerId();

      // High Clouds (> 22,000 ft ASL)
      if (state.liveWeatherSatOverlayHighEnabled && satScan) {
        const highUrl = getSatelliteOverlayTileUrl(state.manifest, 'regional_high_clouds', satScan);
        this.layerManager.updateRasterLayer(
          'live-weather-sat-overlay-high',
          [highUrl],
          state.liveWeatherSatOverlayHighOpacity ?? 0.85,
          firstSymbol,
          0,
          6
        );
      } else {
        this.layerManager.removeLayer('live-weather-sat-overlay-high');
      }

      // Mid Clouds (14,500 - 22,000 ft ASL)
      if (state.liveWeatherSatOverlayMidEnabled && satScan) {
        const midUrl = getSatelliteOverlayTileUrl(state.manifest, 'regional_mid_clouds', satScan);
        this.layerManager.updateRasterLayer(
          'live-weather-sat-overlay-mid',
          [midUrl],
          state.liveWeatherSatOverlayMidOpacity ?? 0.85,
          firstSymbol,
          0,
          6
        );
      } else {
        this.layerManager.removeLayer('live-weather-sat-overlay-mid');
      }

      // Valley Fog Sea (< 3,500 ft ASL)
      if (state.liveWeatherSatOverlayFogEnabled && satScan) {
        const fogUrl = getSatelliteOverlayTileUrl(state.manifest, 'regional_fog_sea', satScan);
        this.layerManager.updateRasterLayer(
          'live-weather-sat-overlay-fog',
          [fogUrl],
          state.liveWeatherSatOverlayFogOpacity ?? 0.85,
          firstSymbol,
          0,
          8
        );
      } else {
        this.layerManager.removeLayer('live-weather-sat-overlay-fog');
      }
    } else {
      this.layerManager.removeLayer('live-weather-satellite-pmtiles');
      this.layerManager.removeLayer('live-weather-sat-overlay-high');
      this.layerManager.removeLayer('live-weather-sat-overlay-mid');
      this.layerManager.removeLayer('live-weather-sat-overlay-fog');
      this.satRenderer.clear();
    }

    // 2. Update CONUS Temperature Gradient Layer
    if (state.liveWeatherTempGradientEnabled) {
      let tempTileUrl = 'https://mesonet.agron.iastate.edu/c/tile.py/1.0.0/rtma::tmpf/{z}/{x}/{y}.png';
      
      if (state.manifest && state.manifest.hrtemp && state.manifest.hrtemp.available_by_model) {
        const blendModel = state.manifest.hrtemp.available_by_model.blend;
        if (blendModel && Object.keys(blendModel).length > 0) {
          const sortedDates = state.manifest.hrtemp.sorted_dates || Object.keys(blendModel).sort();
          const latestDate = sortedDates[sortedDates.length - 1];
          const hours = blendModel[latestDate];
          if (hours && hours.length > 0) {
            const latestHour = hours[hours.length - 1];
            tempTileUrl = getTileUrl(state.manifest, 'hrtemp', 'blend', latestDate, latestHour);
          }
        }
      }

      const firstSymbol = this.getFirstSymbolLayerId();
      this.layerManager.updateRasterLayer(
        'live-weather-temp-gradient',
        [tempTileUrl],
        state.liveWeatherTempGradientOpacity,
        firstSymbol,
        0,
        5
      );
    } else {
      this.layerManager.removeLayer('live-weather-temp-gradient');
    }

    // 3. Update NOAA MRMS Doppler Radar Layer
    if (state.liveWeatherRadarEnabled) {
      if (state.currentTimeStep) {
        const closestIdx = this.service.getClosestRadarFrameIndex(state.currentTimeStep);
        this.service.setCurrentFrameIndex(closestIdx);
      }
      const radarFrame = this.service.getCurrentRadarFrame();
      this.radarRenderer.update(radarFrame, state.liveWeatherRadarOpacity, true);
    } else {
      this.radarRenderer.clear();
    }

    // 3b. Update MRMS Quantitative Precipitation Accumulation (QPE) Layer
    const isPrecipAccumActive = !!state.precipAccumEnabled || state.primaryProduct === 'precip_accum' || state.primaryProduct === 'rain_accum' || !!(state.activeLayerProducts && (state.activeLayerProducts['precip_accum'] || state.activeLayerProducts['rain_accum']));
    const currentQpeMode = isPrecipAccumActive ? 'off' : (state.liveWeatherQpeMode || 'off');
    if (this.lastQpeMode !== currentQpeMode || isPrecipAccumActive) {
      this.layerManager.removeBufferedRasterLayer('live-weather-mrms-qpe');
      this.lastQpeMode = currentQpeMode;
    }

    if (currentQpeMode !== 'off') {
      let qpeTileUrl = '';
      const qpeKey = `mrms_qpe_${currentQpeMode}`;
      if (state.manifest && (state.manifest as any).live_weather && (state.manifest as any).live_weather[qpeKey]) {
        const qpeScans: string[] = (state.manifest as any).live_weather[qpeKey].scans;
        if (qpeScans && qpeScans.length > 0) {
          let activeIndex = qpeScans.length - 1;
          if (state.currentTimeStep) {
            const nowDate = ManifestTimelineHelper.getNowDate(state.timelineRange);
            const diffMs = state.currentTimeStep.getTime() - nowDate.getTime();
            const offsetHours = diffMs / (3600 * 1000);
            if (offsetHours < -0.01) {
              const latestScanMs = parseIsoScanToMs(qpeScans[qpeScans.length - 1]);
              const targetScanMs = latestScanMs + offsetHours * 3600 * 1000;
              let minDiff = Infinity;
              qpeScans.forEach((scan, idx) => {
                const scanMs = parseIsoScanToMs(scan);
                const diff = Math.abs(scanMs - targetScanMs);
                if (diff < minDiff) {
                  minDiff = diff;
                  activeIndex = idx;
                }
              });
            }
          } else if (state.liveWeatherRadarEnabled) {
            const radarFrames = this.service.getRadarFrames();
            if (radarFrames.length > 0) {
              const radarIdx = this.service.getCurrentFrameIndex();
              const ratio = radarIdx / Math.max(1, radarFrames.length - 1);
              activeIndex = Math.min(Math.floor(ratio * qpeScans.length), qpeScans.length - 1);
            }
          }
          const targetScan = qpeScans[activeIndex];
          const tileRoot = (state.manifest.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
          const apiServer = getApiServer(state.manifest);
          const rawPmtiles = cleanUrl(`${apiServer}/${tileRoot}/observed/mrms/mrms_qpe_${currentQpeMode}_${targetScan}.pmtiles`);
          qpeTileUrl = `${buildPmtilesUrl(rawPmtiles)}/{z}/{x}/{y}.webp`;
        }
      }

      if (qpeTileUrl) {
        const firstSymbol = this.getFirstSymbolLayerId();
        this.layerManager.updateBufferedRasterLayer(
          'live-weather-mrms-qpe',
          [qpeTileUrl],
          state.liveWeatherQpeOpacity,
          firstSymbol,
          0,
          5
        );
      } else {
        this.layerManager.removeBufferedRasterLayer('live-weather-mrms-qpe');
      }
    } else {
      this.layerManager.removeBufferedRasterLayer('live-weather-mrms-qpe');
    }



    // 3.5 Update Live Weather Current Sun Intensity Layer
    if (state.liveWeatherSunIntensityEnabled) {
      const current = state.currentTimeStep || new Date();
      const dateStr = current.toISOString().split('T')[0];
      const hourStr = String(current.getUTCHours()).padStart(2, '0');
      const sunTileUrl = state.manifest
        ? getTileUrl(state.manifest, 'sun_intensity', 'rrfs', dateStr, hourStr)
        : `${buildPmtilesUrl(`tiles/sun_intensity_rrfs_${dateStr}_Z${hourStr}.pmtiles`)}/{z}/{x}/{y}.webp`;

      const firstSymbol = this.getFirstSymbolLayerId();
      this.layerManager.updateRasterLayer(
        'live-weather-sun-intensity',
        [sunTileUrl],
        state.liveWeatherSunIntensityOpacity,
        firstSymbol,
        0,
        5
      );
    } else {
      this.layerManager.removeLayer('live-weather-sun-intensity');
    }


    // 4. Update MRMS Vector Lightning Strikes Layer (8h Time-Decay & Density Scaling)
    if (state.liveWeatherRadarLightningEnabled) {
      let historyUrl = '';
      let targetEpochSec = Math.floor(Date.now() / 1000);

      if (state.manifest && (state.manifest as any).live_weather) {
        const lw = (state.manifest as any).live_weather;
        const ltScans: string[] = lw.mrms_lightning?.scans || [];
        const latestLtScan: string =
          lw.mrms_lightning?.latest_scan ||
          (ltScans.length > 0 ? ltScans[ltScans.length - 1] : '') ||
          lw.mrms?.latest_scan ||
          'latest';

        const parsedLatestMs = latestLtScan !== 'latest' ? parseIsoScanToMs(latestLtScan) : 0;
        const latestScanMs = parsedLatestMs > 0 ? parsedLatestMs : Date.now();

        if (state.currentTimeStep) {
          const nowDate = ManifestTimelineHelper.getNowDate(state.timelineRange);
          const offsetMs = Math.min(0, state.currentTimeStep.getTime() - nowDate.getTime());
          targetEpochSec = Math.floor((latestScanMs + offsetMs) / 1000);
        } else if (state.liveWeatherRadarEnabled) {
          const radarFrame = this.service.getCurrentRadarFrame();
          targetEpochSec = radarFrame ? radarFrame.time : Math.floor(latestScanMs / 1000);
        } else if (state.liveWeatherSatelliteEnabled) {
          const satFrame = this.service.getCurrentSatelliteFrame();
          if (satFrame) {
            const satMs = satFrame.time > 10000000000 ? satFrame.time : satFrame.time * 1000;
            targetEpochSec = Math.floor(satMs / 1000);
          } else {
            targetEpochSec = Math.floor(latestScanMs / 1000);
          }
        } else {
          targetEpochSec = Math.floor(latestScanMs / 1000);
        }

        const tileRoot = (state.manifest.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
        const apiServer = getApiServer(state.manifest);
        let rawPath = cleanUrl(
          `${apiServer}/${tileRoot}/observed/mrms/mrms_lightning_history.json?v=${encodeURIComponent(latestLtScan)}`
        );
        if (!rawPath.startsWith('http://') && !rawPath.startsWith('https://')) {
          const origin = typeof window !== 'undefined' ? window.location.origin : '';
          let pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
          pathname = pathname.substring(0, pathname.lastIndexOf('/') + 1);
          const cleanPath = rawPath.startsWith('/') ? rawPath.substring(1) : rawPath;
          rawPath = cleanUrl(`${origin}${pathname}${cleanPath}`);
        }
        historyUrl = appendAuthToken(rawPath);
      }

      const lightningOpacity = state.liveWeatherLightningOpacity !== undefined ? state.liveWeatherLightningOpacity : 1.0;
      const compositeAboveDomCanvases = Boolean(state.liveWeatherRadarEnabled || state.liveWeatherSatelliteEnabled);
      this.lightningRenderer.update(
        historyUrl || null,
        targetEpochSec,
        lightningOpacity,
        true,
        compositeAboveDomCanvases
      );
    } else {
      this.lightningRenderer.clear();
    }



    // 5. Update Air Quality Stations (AirNow + ECCC)
    if (state.liveWeatherAqiStationsEnabled) {
      this.updateAqiStationsLayer(state.liveWeatherAqiStationsOpacity || 0.85);
    } else {
      this.removeAqiStationsLayer();
    }

    // Defensive cleanup: never allow an orphaned interval to keep ticking when stopped or when no animatable observed layers remain
    const anyObservedPlaying = Boolean(state.liveWeatherRadarPlaying || state.liveWeatherSatellitePlaying);
    if (!anyObservedPlaying || !this.hasAnimatableObservedLayers(state)) {
      if (this.observedIntervalId) {
        clearInterval(this.observedIntervalId);
        this.observedIntervalId = null;
      }
      this.isObservedBuffering = false;
    }
  }

  public hasAnimatableObservedLayers(s: any = store.getState()): boolean {
    return Boolean(
      s.liveWeatherRadarEnabled ||
      s.liveWeatherRadarLightningEnabled ||
      s.liveWeatherSatelliteEnabled
    );
  }

  private coerceObservedLoopHours(rawHours?: number): number {
    const opts: number[] =
      (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather?.observed_duration_options) ||
      [2, 4, 8];
    const h = Number(rawHours) || 8;
    if (opts.includes(h)) return h;
    // Snap to closest valid option in [2, 4, 8]
    return opts.reduce((best, cand) => (Math.abs(cand - h) < Math.abs(best - h) ? cand : best), opts[0] || 4);
  }

  public isObservedPlayingOrBuffering(s: any = store.getState()): boolean {
    return Boolean(
      this.isObservedBuffering ||
      s.liveWeatherObservedBuffering ||
      s.liveWeatherRadarPlaying ||
      s.liveWeatherSatellitePlaying
    );
  }

  /**
   * Unified, token-guarded observed playback controller driving Radar, Live Lightning, and Satellite loops.
   * Guarantees at most one active timer at any time, cancels in-flight async pre-buffering cleanly on stop,
   * and self-terminates immediately if all animatable observed layers are deactivated.
   */
  public async startObservedAnimation(preserveCursor: boolean = false): Promise<void> {
    const initialState = store.getState();
    if (!this.active || !this.hasAnimatableObservedLayers(initialState)) {
      this.stopObservedAnimation(false);
      return;
    }

    if (initialState.forecastAnimationPlaying || initialState.forecastAnimationBuffering) {
      ForecastAnimationService.getInstance().stop();
    }

    const currentToken = ++this.observedAnimationToken;
    const wasPlaying = preserveCursor || this.observedIntervalId !== null;
    if (this.observedIntervalId) {
      clearInterval(this.observedIntervalId);
      this.observedIntervalId = null;
    }

    const s0 = store.getState();
    const hasRadarOrLt = Boolean(s0.liveWeatherRadarEnabled || s0.liveWeatherRadarLightningEnabled);
    const hasSat = Boolean(s0.liveWeatherSatelliteEnabled);
    const loopHours = this.coerceObservedLoopHours(
      hasRadarOrLt ? s0.liveWeatherRadarLoopHours : s0.liveWeatherSatelliteLoopHours
    );
    const speedMultiplier =
      Number(hasRadarOrLt ? s0.liveWeatherRadarPlaybackSpeed : s0.liveWeatherSatPlaybackSpeed) || 1.0;

    this.isObservedBuffering = true;

    store.update({
      liveWeatherObservedBuffering: true,
      liveWeatherRadarPlaying: hasRadarOrLt,
      liveWeatherSatellitePlaying: hasSat,
      liveWeatherRadarLoopHours: loopHours,
      liveWeatherSatelliteLoopHours: loopHours,
      liveWeatherRadarPlaybackSpeed: speedMultiplier,
      liveWeatherSatPlaybackSpeed: speedMultiplier,
      activeDockPlaybackMode: 'observed'
    });

    const radarFrames = s0.liveWeatherRadarEnabled ? this.service.getRadarFrames(loopHours) : [];
    const satFrames = hasSat
      ? this.service.getSatelliteFrames(loopHours, s0.liveWeatherSatelliteSources)
      : [];

    const preloadTasks: Promise<void>[] = [];
    if (s0.liveWeatherRadarEnabled && radarFrames.length > 0) {
      preloadTasks.push(this.radarRenderer.waitForFrames(radarFrames, 8, 1000));
    }
    if (s0.liveWeatherSatelliteEnabled && satFrames.length > 0) {
      preloadTasks.push(this.satRenderer.waitForFrames(satFrames, 8, 1000));
    }
    if (preloadTasks.length > 0) {
      await Promise.all(preloadTasks);
    }

    // Abort if stopped, paused, deactivated, or superseded while awaiting frame pre-buffering
    if (currentToken !== this.observedAnimationToken) {
      return;
    }
    if (this.observedIntervalId) {
      clearInterval(this.observedIntervalId);
      this.observedIntervalId = null;
    }
    this.isObservedBuffering = false;

    const s1 = store.getState();
    const stillPlaying = Boolean(s1.liveWeatherRadarPlaying || s1.liveWeatherSatellitePlaying);
    if (!this.active || !stillPlaying || !this.hasAnimatableObservedLayers(s1)) {
      if (s1.liveWeatherObservedBuffering || s1.liveWeatherRadarPlaying || s1.liveWeatherSatellitePlaying) {
        store.update({
          liveWeatherObservedBuffering: false,
          liveWeatherRadarPlaying: false,
          liveWeatherSatellitePlaying: false
        });
      }
      return;
    }

    if (s1.liveWeatherObservedBuffering) {
      store.update({ liveWeatherObservedBuffering: false });
    }

    const nowDate = ManifestTimelineHelper.getNowDate(s1.timelineRange);
    // Use 2-min radar driver only when Doppler Radar itself is enabled; Live Lightning alone steps at native 5-min scan cadence
    const useRadarDriver = Boolean(s1.liveWeatherRadarEnabled) && radarFrames.length > 0;
    const useSatDriver = !useRadarDriver && Boolean(s1.liveWeatherSatelliteEnabled) && satFrames.length > 0;

    if (useRadarDriver) {
      const latestScanSec = radarFrames[radarFrames.length - 1].time;
      if (!wasPlaying) {
        const oldestFrame = radarFrames[0];
        const allRadarFrames = this.service.getRadarFrames();
        const globalIdx = allRadarFrames.findIndex(f => f.time === oldestFrame.time);
        this.service.setCurrentFrameIndex(globalIdx !== -1 ? globalIdx : 0);
        const offsetMs = (oldestFrame.time - latestScanSec) * 1000;
        this.updateStoreAndRefreshOnce({ currentTimeStep: new Date(nowDate.getTime() + offsetMs) });
      }

      const intervalMs = Math.max(25, Math.round(100 / speedMultiplier));
      this.observedIntervalId = setInterval(() => {
        const cur = store.getState();
        const anyPlaying = Boolean(cur.liveWeatherRadarPlaying || cur.liveWeatherSatellitePlaying);
        if (
          currentToken !== this.observedAnimationToken ||
          !this.active ||
          !anyPlaying ||
          !this.hasAnimatableObservedLayers(cur)
        ) {
          if (this.observedIntervalId) {
            clearInterval(this.observedIntervalId);
            this.observedIntervalId = null;
          }
          if (!this.hasAnimatableObservedLayers(cur) && anyPlaying) {
            store.update({
              liveWeatherRadarPlaying: false,
              liveWeatherSatellitePlaying: false,
              liveWeatherObservedBuffering: false
            });
          }
          return;
        }

        const freshNow = ManifestTimelineHelper.getNowDate(cur.timelineRange);
        this.service.nextFrame(loopHours);
        const frame = this.service.getCurrentRadarFrame();
        if (frame && latestScanSec > 0) {
          const offsetMs = (frame.time - latestScanSec) * 1000;
          this.updateStoreAndRefreshOnce({ currentTimeStep: new Date(freshNow.getTime() + offsetMs) });
        } else {
          this.refresh();
        }
      }, intervalMs);
      return;
    }

    if (useSatDriver) {
      const latestSatTime =
        satFrames[satFrames.length - 1].time > 10000000000
          ? satFrames[satFrames.length - 1].time
          : satFrames[satFrames.length - 1].time * 1000;

      if (!wasPlaying) {
        const oldestFrame = satFrames[0];
        const oldestFrameMs = oldestFrame.time > 10000000000 ? oldestFrame.time : oldestFrame.time * 1000;
        const allSatFrames = this.service.getSatelliteFrames(undefined, s1.liveWeatherSatelliteSources);
        const globalIdx = allSatFrames.findIndex(f => f.time === oldestFrame.time);
        this.service.setSatelliteFrameIndex(globalIdx !== -1 ? globalIdx : 0);
        const offsetMs = oldestFrameMs - latestSatTime;
        this.updateStoreAndRefreshOnce({ currentTimeStep: new Date(nowDate.getTime() + offsetMs) });
      }

      const intervalMs = Math.max(25, Math.round(200 / speedMultiplier));
      this.observedIntervalId = setInterval(() => {
        const cur = store.getState();
        const anyPlaying = Boolean(cur.liveWeatherRadarPlaying || cur.liveWeatherSatellitePlaying);
        if (
          currentToken !== this.observedAnimationToken ||
          !this.active ||
          !anyPlaying ||
          !this.hasAnimatableObservedLayers(cur)
        ) {
          if (this.observedIntervalId) {
            clearInterval(this.observedIntervalId);
            this.observedIntervalId = null;
          }
          if (!this.hasAnimatableObservedLayers(cur) && anyPlaying) {
            store.update({
              liveWeatherRadarPlaying: false,
              liveWeatherSatellitePlaying: false,
              liveWeatherObservedBuffering: false
            });
          }
          return;
        }

        const freshNow = ManifestTimelineHelper.getNowDate(cur.timelineRange);
        this.service.nextSatelliteFrame(loopHours);
        const frame = this.service.getCurrentSatelliteFrame();
        if (frame && latestSatTime > 0) {
          const frameMs = frame.time > 10000000000 ? frame.time : frame.time * 1000;
          const offsetMs = frameMs - latestSatTime;
          this.updateStoreAndRefreshOnce({ currentTimeStep: new Date(freshNow.getTime() + offsetMs) });
        } else {
          this.refresh();
        }
      }, intervalMs);
      return;
    }

    // Native 5-minute scan stepping for Live Lightning (or before radar frames populate)
    const windowMs = loopHours * 3600 * 1000;
    const stepMs = 5 * 60 * 1000;
    let cursorOffsetMs = -windowMs;
    if (!wasPlaying) {
      this.updateStoreAndRefreshOnce({ currentTimeStep: new Date(nowDate.getTime() + cursorOffsetMs) });
    } else if (s1.currentTimeStep) {
      cursorOffsetMs = Math.max(-windowMs, Math.min(0, s1.currentTimeStep.getTime() - nowDate.getTime()));
    }

    const intervalMs = Math.max(35, Math.round(180 / speedMultiplier));
    this.observedIntervalId = setInterval(() => {
      const cur = store.getState();
      const anyPlaying = Boolean(cur.liveWeatherRadarPlaying || cur.liveWeatherSatellitePlaying);
      if (
        currentToken !== this.observedAnimationToken ||
        !this.active ||
        !anyPlaying ||
        !this.hasAnimatableObservedLayers(cur)
      ) {
        if (this.observedIntervalId) {
          clearInterval(this.observedIntervalId);
          this.observedIntervalId = null;
        }
        if (!this.hasAnimatableObservedLayers(cur) && anyPlaying) {
          store.update({
            liveWeatherRadarPlaying: false,
            liveWeatherSatellitePlaying: false,
            liveWeatherObservedBuffering: false
          });
        }
        return;
      }

      const freshNow = ManifestTimelineHelper.getNowDate(cur.timelineRange);
      cursorOffsetMs += stepMs;
      if (cursorOffsetMs > 0) {
        cursorOffsetMs = -windowMs;
      }
      this.updateStoreAndRefreshOnce({ currentTimeStep: new Date(freshNow.getTime() + cursorOffsetMs) });
    }, intervalMs);
  }

  public stopObservedAnimation(snapToNow: boolean = true): void {
    this.observedAnimationToken++;
    this.isObservedBuffering = false;
    if (this.observedIntervalId) {
      clearInterval(this.observedIntervalId);
      this.observedIntervalId = null;
    }

    const state = store.getState();
    if (snapToNow) {
      const radarFrames = this.service.getRadarFrames();
      if (radarFrames.length > 0) {
        this.service.setCurrentFrameIndex(radarFrames.length - 1);
      }
      const satFrames = this.service.getSatelliteFrames(undefined, state.liveWeatherSatelliteSources);
      if (satFrames.length > 0) {
        this.service.setSatelliteFrameIndex(satFrames.length - 1);
      }
    }

    const updates: any = {};
    if (state.liveWeatherRadarPlaying) updates.liveWeatherRadarPlaying = false;
    if (state.liveWeatherSatellitePlaying) updates.liveWeatherSatellitePlaying = false;
    if (state.liveWeatherObservedBuffering) updates.liveWeatherObservedBuffering = false;
    if (snapToNow && !state.forecastAnimationPlaying) {
      updates.currentTimeStep = ManifestTimelineHelper.getNowDate(state.timelineRange);
    }
    if (Object.keys(updates).length > 0) {
      this.updateStoreAndRefreshOnce(updates);
    } else if (this.active) {
      this.refresh();
    }
  }

  public pauseObservedAnimation(): void {
    this.observedAnimationToken++;
    this.isObservedBuffering = false;
    if (this.observedIntervalId) {
      clearInterval(this.observedIntervalId);
      this.observedIntervalId = null;
    }
    const state = store.getState();
    if (state.liveWeatherRadarPlaying || state.liveWeatherSatellitePlaying || state.liveWeatherObservedBuffering) {
      this.updateStoreAndRefreshOnce({
        liveWeatherRadarPlaying: false,
        liveWeatherSatellitePlaying: false,
        liveWeatherObservedBuffering: false
      });
    } else if (this.active) {
      this.refresh();
    }
  }

  public async startAnimation(): Promise<void> {
    await this.startObservedAnimation(false);
  }

  public stopAnimation(): void {
    this.stopObservedAnimation(true);
  }

  public setFrameIndex(index: number, updateGlobalStore: boolean = true): void {
    this.service.setCurrentFrameIndex(index);
    if (this.active && (store.getState().liveWeatherRadarEnabled || store.getState().liveWeatherRadarLightningEnabled)) {
      if (updateGlobalStore) {
        const frame = this.service.getCurrentRadarFrame();
        const radarFrames = this.service.getRadarFrames();
        const latestScanSec = radarFrames.length > 0 ? radarFrames[radarFrames.length - 1].time : 0;
        if (frame && latestScanSec > 0) {
          const nowDate = ManifestTimelineHelper.getNowDate(store.getState().timelineRange);
          const offsetMs = (frame.time - latestScanSec) * 1000;
          this.updateStoreAndRefreshOnce({ currentTimeStep: new Date(nowDate.getTime() + offsetMs) });
          return;
        }
      }
      this.refresh();
    }
  }

  public async startSatelliteAnimation(): Promise<void> {
    await this.startObservedAnimation(false);
  }

  public stopSatelliteAnimation(): void {
    this.stopObservedAnimation(true);
  }

  public pauseAnimation(): void {
    this.pauseObservedAnimation();
  }

  public pauseSatelliteAnimation(): void {
    this.pauseObservedAnimation();
  }

  public setSatelliteFrameIndex(index: number, updateGlobalStore: boolean = true): void {
    this.service.setSatelliteFrameIndex(index);
    if (this.active && store.getState().liveWeatherSatelliteEnabled) {
      if (updateGlobalStore) {
        const frame = this.service.getCurrentSatelliteFrame();
        const satSources = store.getState().liveWeatherSatelliteSources || [store.getState().liveWeatherSatelliteSource || 'goes_composite'];
        const frames = this.service.getSatelliteFrames(undefined, satSources);
        const latestSatTime = frames.length > 0
          ? (frames[frames.length - 1].time > 10000000000 ? frames[frames.length - 1].time : frames[frames.length - 1].time * 1000)
          : 0;
        if (frame && latestSatTime > 0) {
          const nowDate = ManifestTimelineHelper.getNowDate(store.getState().timelineRange);
          const frameMs = frame.time > 10000000000 ? frame.time : frame.time * 1000;
          const offsetMs = frameMs - latestSatTime;
          this.updateStoreAndRefreshOnce({ currentTimeStep: new Date(nowDate.getTime() + offsetMs) });
          return;
        }
      }
      this.refresh();
    }
  }


  private handleMapMove(): void {
    if (!this.active) return;
    if (store.getState().liveWeatherRadarEnabled) {
      this.radarRenderer.redraw();
    }
    if (store.getState().liveWeatherSatelliteEnabled) {
      this.satRenderer.redraw();
    }
    if (store.getState().liveWeatherRadarLightningEnabled) {
      this.lightningRenderer.redraw();
    }
  }

  private getAqiFilterExpression(): any {
    const appCfg = (typeof __APP_CONFIG__ !== 'undefined' ? (__APP_CONFIG__ as any)?.aqi_stations : undefined) || {};
    const r2Zoom = Number(appCfg.zoom_rank2) || 5.0;
    const r3Zoom = Number(appCfg.zoom_rank3) || 6.5;
    const r4Zoom = Number(appCfg.zoom_rank4) || 8.0;
    const hazardAqi = Number(appCfg.always_show_hazard_aqi) || 101;
    return [
      'any',
      ['>=', ['coalesce', ['get', 'aqi'], 0], hazardAqi],
      ['>=', ['coalesce', ['get', 'aqhi'], 0], 7],
      ['<=', ['coalesce', ['get', 'rank'], 4], ['step', ['zoom'], 1, r2Zoom, 2, r3Zoom, 3, r4Zoom, 4]]
    ];
  }

  private updateAqiStationsLayer(opacity: number): void {
    const state = store.getState();
    const mtime = Number((state.manifest as any)?.aqi_stations?.mtime_epoch) || 0;
    const cachedGeoJson = this.service.getCachedAqiStationsGeoJson();
    const mtimeChanged = mtime > 0 && mtime !== this.lastAqiMtimeEpoch;

    if (!this.map.getSource('live-weather-aqi-stations-source')) {
      if (cachedGeoJson) {
        this.lastAqiMtimeEpoch = mtime;
      }
      this.map.addSource('live-weather-aqi-stations-source', {
        type: 'geojson',
        data: cachedGeoJson || { type: 'FeatureCollection', features: [] },
        cluster: false,
        tolerance: 0,
        buffer: 64,
        maxzoom: 9
      });
    }

    if (!cachedGeoJson || mtimeChanged) {
      void this.service.fetchAqiStationsGeoJson(mtimeChanged).then((geoJson) => {
        if (!geoJson) return;
        this.lastAqiMtimeEpoch = mtime;
        const src = this.map.getSource('live-weather-aqi-stations-source') as maplibregl.GeoJSONSource | undefined;
        if (src && typeof src.setData === 'function') {
          src.setData(geoJson);
        }
      });
    }

    const aqiFilter = this.getAqiFilterExpression();
    const appCfg = (typeof __APP_CONFIG__ !== 'undefined' ? (__APP_CONFIG__ as any)?.aqi_stations : undefined) || {};
    const labelMinZoom = Number(appCfg.label_min_zoom) || 6.5;
    const labelsEnabled = state.aqiStationLabelsEnabled !== false;
    const firstSymbol = this.getFirstSymbolLayerId();

    if (!this.map.getLayer('live-weather-aqi-stations-circles')) {
      const circleColorExpr: any = [
        'case',
        ['==', ['typeof', ['get', 'aqi']], 'number'],
        [
          'step',
          ['get', 'aqi'],
          '#00e400', 51,
          '#ffff00', 101,
          '#ff7e00', 151,
          '#ff0000', 201,
          '#8f3f97', 301,
          '#7e0023'
        ],
        ['==', ['typeof', ['get', 'aqhi']], 'number'],
        [
          'step',
          ['get', 'aqhi'],
          '#00ccff', 4,
          '#ffcc00', 7,
          '#ff6600', 11,
          '#990000'
        ],
        '#808080'
      ];

      this.map.addLayer({
        id: 'live-weather-aqi-stations-circles',
        type: 'circle',
        source: 'live-weather-aqi-stations-source',
        filter: aqiFilter,
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            3, 3.5,
            6, 5.5,
            10, 8.5
          ] as any,
          'circle-color': circleColorExpr as any,
          'circle-stroke-color': '#000000',
          'circle-stroke-width': 1.1,
          'circle-opacity': opacity,
          'circle-stroke-opacity': 0.85
        }
      }, firstSymbol);
    } else {
      this.map.setFilter('live-weather-aqi-stations-circles', aqiFilter);
      this.map.setPaintProperty('live-weather-aqi-stations-circles', 'circle-opacity', opacity);
      this.map.setLayoutProperty('live-weather-aqi-stations-circles', 'visibility', 'visible');
    }

    if (!this.map.getLayer('live-weather-aqi-stations-labels')) {
      this.map.addLayer({
        id: 'live-weather-aqi-stations-labels',
        type: 'symbol',
        source: 'live-weather-aqi-stations-source',
        minzoom: labelMinZoom,
        filter: aqiFilter,
        layout: {
          'visibility': labelsEnabled ? 'visible' : 'none',
          'text-font': ['Noto Sans Regular'],
          'text-field': [
            'case',
            ['==', ['typeof', ['get', 'aqi']], 'number'],
            ['to-string', ['round', ['get', 'aqi']]],
            ['==', ['typeof', ['get', 'aqhi']], 'number'],
            ['to-string', ['get', 'aqhi']],
            ''
          ] as any,
          'text-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6.5, 10,
            10, 11.5
          ] as any,
          'symbol-sort-key': ['coalesce', ['get', 'rank'], 4] as any,
          'text-variable-anchor': ['bottom', 'top', 'right', 'left'],
          'text-radial-offset': 0.55,
          'text-allow-overlap': false,
          'text-ignore-placement': false,
          'text-padding': 3
        },
        paint: {
          'text-color': '#f8fafc',
          'text-halo-color': 'rgba(9, 13, 22, 0.92)',
          'text-halo-width': 1.6,
          'text-opacity': Math.min(1, opacity + 0.1)
        }
      });
    } else {
      this.map.setFilter('live-weather-aqi-stations-labels', aqiFilter);
      this.map.setPaintProperty('live-weather-aqi-stations-labels', 'text-opacity', Math.min(1, opacity + 0.1));
      this.map.setLayoutProperty('live-weather-aqi-stations-labels', 'visibility', labelsEnabled ? 'visible' : 'none');
    }

    if (!this.aqiInteractionsBound) {
      this.setupAqiInteractions();
      this.aqiInteractionsBound = true;
    }
  }

  private removeAqiStationsLayer(): void {
    if (this.map.getLayer('live-weather-aqi-stations-circles')) {
      this.map.setLayoutProperty('live-weather-aqi-stations-circles', 'visibility', 'none');
    }
    if (this.map.getLayer('live-weather-aqi-stations-labels')) {
      this.map.setLayoutProperty('live-weather-aqi-stations-labels', 'visibility', 'none');
    }
    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
  }

  private setupAqiInteractions(): void {
    const layerIds = ['live-weather-aqi-stations-circles', 'live-weather-aqi-stations-labels'];

    const onEnter = (e: any) => {
      this.map.getCanvas().style.cursor = 'pointer';
      const feature = e.features && e.features[0];
      if (!feature) return;

      const props = feature.properties || {};
      const coords = (feature.geometry as any).coordinates.slice();

      const name = props.name || 'Air Quality Station';
      const id = props.id || '';
      const src = props.src || 'Station';
      const aqi = props.aqi !== undefined && props.aqi !== null ? props.aqi : null;
      const aqhi = props.aqhi !== undefined && props.aqhi !== null ? props.aqhi : null;
      const param = props.param || '';
      const cat = props.cat || 'Unknown';
      const ts = props.ts || '';

      const isAirNow = src === 'AIRNOW' || (aqi !== null && aqi !== undefined);
      const scoreVal = isAirNow ? (aqi !== null && aqi !== undefined ? aqi : 'N/A') : (aqhi !== null && aqhi !== undefined ? aqhi : 'N/A');
      const scoreUnit = isAirNow ? 'AQI' : 'AQHI';
      const srcLabel = isAirNow ? '🇺🇸 US AirNow (EPA)' : '🇨🇦 Canada ECCC (AQHI)';

      let catBg = '#00e400';
      let catFg = '#000000';

      if (isAirNow) {
        if (typeof aqi === 'number') {
          if (aqi <= 50) { catBg = '#00e400'; catFg = '#000'; }
          else if (aqi <= 100) { catBg = '#ffff00'; catFg = '#000'; }
          else if (aqi <= 150) { catBg = '#ff7e00'; catFg = '#fff'; }
          else if (aqi <= 200) { catBg = '#ff0000'; catFg = '#fff'; }
          else if (aqi <= 300) { catBg = '#8f3f97'; catFg = '#fff'; }
          else { catBg = '#7e0023'; catFg = '#fff'; }
        }
      } else {
        if (typeof aqhi === 'number') {
          if (aqhi <= 3) { catBg = '#00ccff'; catFg = '#000'; }
          else if (aqhi <= 6) { catBg = '#ffcc00'; catFg = '#000'; }
          else if (aqhi <= 10) { catBg = '#ff6600'; catFg = '#fff'; }
          else { catBg = '#990000'; catFg = '#fff'; }
        }
      }

      let formattedTs = ts;
      try {
        const d = new Date(ts);
        if (!isNaN(d.getTime())) {
          formattedTs = d.toISOString().replace('T', ' ').substring(0, 16) + ' UTC';
        }
      } catch (err) {}

      let scaleHtml = '';
      if (isAirNow) {
        scaleHtml = `
          <div style="margin-top: 8px; font-size: 0.68rem; color: #a1a1aa; font-weight: 600;">US EPA AQI Reference Scale:</div>
          <div style="display: flex; height: 6px; border-radius: 3px; overflow: hidden; margin-top: 3px; border: 1px solid rgba(255,255,255,0.1);">
            <div style="flex: 50; background: #00e400;" title="0-50 Good"></div>
            <div style="flex: 50; background: #ffff00;" title="51-100 Moderate"></div>
            <div style="flex: 50; background: #ff7e00;" title="101-150 USG"></div>
            <div style="flex: 50; background: #ff0000;" title="151-200 Unhealthy"></div>
            <div style="flex: 100; background: #8f3f97;" title="201-300 Very Unhealthy"></div>
            <div style="flex: 200; background: #7e0023;" title="301-500 Hazardous"></div>
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 0.62rem; color: #71717a; margin-top: 3px;">
            <span>0</span><span>50</span><span>100</span><span>150</span><span>200</span><span>300</span><span>500</span>
          </div>
        `;
      } else {
        scaleHtml = `
          <div style="margin-top: 8px; font-size: 0.68rem; color: #a1a1aa; font-weight: 600;">Canada ECCC AQHI Reference Scale:</div>
          <div style="display: flex; height: 6px; border-radius: 3px; overflow: hidden; margin-top: 3px; border: 1px solid rgba(255,255,255,0.1);">
            <div style="flex: 3; background: #00ccff;" title="1-3 Low Risk"></div>
            <div style="flex: 3; background: #ffcc00;" title="4-6 Moderate Risk"></div>
            <div style="flex: 4; background: #ff6600;" title="7-10 High Risk"></div>
            <div style="flex: 2; background: #990000;" title="10+ Very High Risk"></div>
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 0.62rem; color: #71717a; margin-top: 3px;">
            <span>1 (Low)</span><span>4 (Mod)</span><span>7 (High)</span><span>10+ (V.High)</span>
          </div>
        `;
      }

      const html = `
        <div style="padding: 4px; font-family: system-ui, sans-serif; min-width: 220px;">
          <div style="font-size: 0.88rem; font-weight: 700; color: #ffffff; margin-bottom: 2px;">${name}</div>
          <div style="font-size: 0.72rem; color: #a1a1aa; margin-bottom: 8px;">ID: ${id} • ${srcLabel}</div>
          
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 6px; padding: 6px 10px; background: rgba(0,0,0,0.35); border-radius: 8px; border: 1px solid rgba(255,255,255,0.1);">
            <div style="display: flex; align-items: baseline; gap: 4px;">
              <span style="font-size: 1.5rem; font-weight: 900; color: ${catBg}; line-height: 1;">${scoreVal}</span>
              <span style="font-size: 0.78rem; font-weight: 700; color: #f8fafc;">${scoreUnit} ${param ? `(${param})` : ''}</span>
            </div>
            <span style="background: ${catBg}; color: ${catFg}; font-weight: 800; font-size: 0.72rem; padding: 4px 8px; border-radius: 4px; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">${cat}</span>
          </div>

          <div style="font-size: 0.7rem; color: #71717a; margin-bottom: 4px;">🕒 Updated: ${formattedTs}</div>
          
          ${scaleHtml}
          
          <div style="margin-top: 8px; font-size: 0.68rem; color: #00d4ff; font-weight: 600; text-align: center; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 4px;">
            💡 Click station to open AQI & Smoke forecast trend
          </div>
        </div>
      `;

      if (this.hoverPopup) this.hoverPopup.remove();

      this.hoverPopup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'aqi-station-popup'
      })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(this.map);
    };

    const onLeave = () => {
      this.map.getCanvas().style.cursor = '';
      if (this.hoverPopup) {
        this.hoverPopup.remove();
        this.hoverPopup = null;
      }
    };

    const onClick = (e: any) => {
      const feature = e.features && e.features[0];
      if (!feature) return;

      const props = feature.properties || {};
      const coords = (feature.geometry as any).coordinates;
      const lon = coords[0];
      const lat = coords[1];

      aqiStationModal.open(props, lat, lon);
    };

    for (const lid of layerIds) {
      this.map.on('mouseenter', lid, onEnter);
      this.map.on('mouseleave', lid, onLeave);
      this.map.on('click', lid, onClick);
    }
  }
}

export default LiveWeatherLayer;
